import { Button } from '../../components/ui/button';
import { ChannelIcon } from '../../components/ui/ChannelIcon';
import { cn } from '../../lib/utils';
import { destinationById } from '../../lib/publishing/destinations';
import type { PostDestinationId } from '../../lib/publishing/destinations';
import type { ComposerTarget } from './composerState';

// Publishing composer §E — the date & time picker, supporting BOTH:
//   1. apply-to-all — one date/time for every destination (the common case, and
//      the only behaviour before this batch), and
//   2. per-destination custom times — each destination carrying its own.
//
// The model behind it (§F2): the post-level `scheduledFor` is the default, and a
// destination's own `scheduledFor` overrides it when set. That is Zernio's own
// `platforms[].scheduledFor` semantics, so "custom" rows send an override and
// the rest send nothing at all.
//
// A row showing the post-level time is showing an INHERITED value, not a copy —
// changing the top field moves every inheriting destination. "Custom" detaches
// one row; "Reset" re-attaches it. That distinction is why each row states
// which of the two it is rather than just showing a time.

export function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function ScheduleSection({
  scheduledFor,
  targets,
  disabled,
  onChangeAll,
  onChangeTarget,
}: {
  scheduledFor: string | null;
  targets: ComposerTarget[];
  disabled?: boolean;
  onChangeAll: (iso: string | null) => void;
  onChangeTarget: (id: PostDestinationId, iso: string | null) => void;
}) {
  const customCount = targets.filter((t) => t.scheduledFor !== null).length;

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3">
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-500" htmlFor="schedule-all">
          Date &amp; time — all destinations
        </label>
        <input
          id="schedule-all"
          type="datetime-local"
          value={isoToLocalInput(scheduledFor)}
          disabled={disabled}
          className="block h-9 w-full rounded-md border border-slate-200 px-2 text-sm"
          onChange={(e) => onChangeAll(localInputToIso(e.target.value))}
        />
        {customCount > 0 && (
          <p className="mt-1 text-[11px] text-amber-600">
            {customCount} destination{customCount === 1 ? '' : 's'} on a custom time — this field
            won&apos;t move {customCount === 1 ? 'it' : 'them'}.
          </p>
        )}
      </div>

      {targets.length > 0 && (
        <div className="space-y-1.5 border-t border-slate-100 pt-3">
          <p className="text-xs font-medium text-slate-500">Per destination</p>
          {targets.map((t) => (
            <ScheduleRow
              key={t.destination}
              target={t}
              inherited={scheduledFor}
              disabled={disabled}
              onChange={(iso) => onChangeTarget(t.destination, iso)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ScheduleRow({
  target,
  inherited,
  disabled,
  onChange,
}: {
  target: ComposerTarget;
  inherited: string | null;
  disabled?: boolean;
  onChange: (iso: string | null) => void;
}) {
  const destination = destinationById(target.destination);
  const custom = target.scheduledFor !== null;

  return (
    <div className="flex items-center gap-2">
      <span className="flex w-40 shrink-0 items-center gap-1.5 text-xs text-slate-600">
        <ChannelIcon channel={destination.platform} className="h-3.5 w-3.5" />
        <span className="truncate">{destination.label}</span>
      </span>

      {custom ? (
        <>
          <input
            type="datetime-local"
            aria-label={`${destination.label} date and time`}
            value={isoToLocalInput(target.scheduledFor)}
            disabled={disabled}
            className="h-8 min-w-0 flex-1 rounded-md border border-slate-200 px-2 text-sm"
            onChange={(e) => onChange(localInputToIso(e.target.value))}
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={disabled}
            className="h-8 shrink-0 px-2 text-xs"
            onClick={() => onChange(null)}
          >
            Reset
          </Button>
        </>
      ) : (
        <>
          <span className={cn('min-w-0 flex-1 truncate text-xs', inherited ? 'text-slate-500' : 'text-slate-400')}>
            {inherited ? formatInherited(inherited) : 'No date set'}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled}
            className="h-8 shrink-0 px-2 text-xs"
            // Seed from the inherited time so "custom" starts where the AM
            // already is, rather than as an empty box they have to retype.
            onClick={() => onChange(inherited ?? new Date().toISOString())}
          >
            Custom
          </Button>
        </>
      )}
    </div>
  );
}

function formatInherited(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
